import { message } from 'antd'
import { fetchUtil as fetch, buildQuery } from 'src/utils/fetch'
import { saveAuditData } from 'src/api/audit'

const ARENA_BASE = '/arena'

export const getArenaGames = async (): Promise<ArenaGame[]> => {
  try {
    const { data } = await fetch(ARENA_BASE, {
      headers: {
        role: 'ADMIN',
      },
    })
    
    const games = data?.data ?? data
    return Array.isArray(games) ? (games as ArenaGame[]) : []
  } catch (e) {
    message.error('Get Arena Games error!')
    return []
  }
}

export const getDailyChallenges = async (
  params: DailyChallengeQuery = {}
): Promise<DailyChallengePage> => {
  const empty: DailyChallengePage = {
    data: [],
    total: 0,
    currentPage: 0,
    totalPages: 0,
  }
  try {
    const query = new URLSearchParams()
    query.set('page', String(params.page ?? 0))
    query.set('size', String(params.size ?? 10))
    if (params.status) query.set('status', params.status)
    if (params.gameId) query.set('gameId', params.gameId)
    if (params.startFrom) query.set('startFrom', params.startFrom)
    if (params.startTo) query.set('startTo', params.startTo)

    const { data } = await fetch(
      `${ARENA_BASE}/admin/daily/challenges?${query.toString()}`,
      {
        headers: {
          role: 'ADMIN',
        },
      }
    )
    return {
      data: Array.isArray(data?.data) ? (data.data as DailyChallenge[]) : [],
      total: data?.total_items ?? 0,
      currentPage: data?.current_page ?? (params.page ?? 0),
      totalPages: data?.total_pages ?? 0,
    }
  } catch (e) {
    message.error('Get Daily Challenges error!')
    return empty
  }
}

export const saveEditDailyChallenge = async (
  challengeId: string,
  payload: DailyChallengeEditPayload
): Promise<'success' | 'failed'> => {
  try {
    await fetch(`${ARENA_BASE}/daily/challenges/${challengeId}/edit`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        role: 'ADMIN',
      },
    })
    saveAuditData('EDIT', 'ARENA_DAILY_CHALLENGE', challengeId)
    message.success('Daily Challenge saved!')
    return 'success'
  } catch (e) {
    message.error('Edit Daily Challenge error!')
    return 'failed'
  }
}

export const createDailyChallenge = async (
  payload: DailyChallengeCreatePayload
): Promise<'success' | 'failed'> => {
  try {
    await fetch(`${ARENA_BASE}/admin/daily/challenges`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        role: 'ADMIN',
      },
    })
    saveAuditData('CREATE', 'ARENA_DAILY_CHALLENGE', payload.gameId)
    message.success('Daily Challenge created!')
    return 'success'
  } catch (e) {
    message.error('Create Daily Challenge error!')
    return 'failed'
  }
}

export interface ArenaGameEditPayload {
  gameId: string
  status?: ArenaGameStatus
  thumbnailImage?: File | null
  innerBannerImage?: File | null
  dailyChallengeImage?: File | null
  bannerHolderImage?: File | null
  maintenanceImage?: File | null
  comingSoonImage?: File | null
  splashImage?: File | null
  video?: File | null
}

export const saveEditArenaGame = async (
  payload: ArenaGameEditPayload
): Promise<'success' | 'failed'> => {
  try {
    const input = new FormData()
    input.append(
      'arenaGameEditDto',
      new Blob(
        [JSON.stringify({ gameId: payload.gameId, status: payload.status })],
        { type: 'application/json' }
      )
    )
    if (payload.thumbnailImage) input.append('thumbnail', payload.thumbnailImage)
    if (payload.innerBannerImage) input.append('innerBanner', payload.innerBannerImage)
    if (payload.dailyChallengeImage)
      input.append('dailyChallengeBanner', payload.dailyChallengeImage)
    if (payload.bannerHolderImage) input.append('bannerHolder', payload.bannerHolderImage)
    if (payload.maintenanceImage) input.append('maintenance', payload.maintenanceImage)
    if (payload.comingSoonImage) input.append('comingSoon', payload.comingSoonImage)
    if (payload.splashImage) input.append('splash', payload.splashImage)
    if (payload.video) input.append('video', payload.video)

    await fetch(`${ARENA_BASE}/${payload.gameId}/edit`, {
      method: 'POST',
      body: input,
      headers: {
        role: 'ADMIN',
      },
    })
    saveAuditData('EDIT', 'ARENA_GAME', payload.gameId)
    message.success('Arena Game saved!')
    return 'success'
  } catch (e) {
    message.error('Edit Arena Game error!')
    return 'failed'
  }
}

export const updateArenaGameStatus = async (
  gameId: string,
  status: ArenaGameStatus
): Promise<'success' | 'failed'> => {
  return saveEditArenaGame({ gameId, status })
}

const REPORT_BASE = '/report/arena-tournament-report'

const buildReportBody = (
  params: TournamentReportQuery
): Record<string, string> => {
  const body: Record<string, string> = {}
  if (params.gameId) body.gameId = params.gameId
  if (params.gameMode) body.gameMode = params.gameMode
  if (params.state) body.state = params.state
  if (params.startDate) body.startDate = params.startDate
  if (params.endDate) body.endDate = params.endDate
  return body
}

export const searchTournamentReport = async (
  params: TournamentReportQuery = {}
): Promise<TournamentReportPage> => {
  const empty: TournamentReportPage = {
    data: [],
    total: 0,
    currentPage: 0,
    totalPages: 0,
  }
  try {
    const query = buildQuery({
      page: params.page ?? 0,
      pageSize: params.pageSize ?? 10,
    })
    const { data } = await fetch(`${REPORT_BASE}/search?${query}`, {
      method: 'POST',
      body: JSON.stringify(buildReportBody(params)),
    })
    return {
      data: Array.isArray(data?.data) ? (data.data as TournamentReportRow[]) : [],
      total: data?.total_items ?? 0,
      currentPage: data?.current_page ?? (params.page ?? 0),
      totalPages: data?.total_pages ?? 0,
    }
  } catch (e) {
    message.error('Get Tournaments report error!')
    return empty
  }
}

const todayStamp = (): string => {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${dd}-${mm}-${yyyy}`
}

export const exportTournamentReport = async (
  format: 'pdf' | 'csv',
  params: TournamentReportQuery = {}
): Promise<void> => {
  try {
    await fetch(`${REPORT_BASE}/generate/${format}`, {
      method: 'POST',
      body: JSON.stringify(buildReportBody(params)),
      responseType: 'blob',
      filename: `TournamentReport_${todayStamp()}.${format}`,
      ...(format === 'csv' ? { contentType: 'text/csv' } : {}),
    })
  } catch (e) {
    message.error('Export Tournaments report error!')
  }
}
